import { getAuthConfig } from './config.js';
import type { AuthUser } from './types.js';
import type { GoogleRedirectConfig } from './google.js';

/**
 * Typed facade over the platform auth API — the ONE place the apps touch auth.
 *
 * Verified contract (platform src/handlers/password-auth.ts + user-info.ts):
 *  - Login:  POST /api/v1/auth/login  → { data: { user, accessToken, refreshToken } }
 *  - Verify: GET  /api/v1/userInfo    (Authorization: Bearer) → { data: { user } }
 *
 * Tokens are managed HERE in localStorage (not via the SDK, whose setTokens/getAuthToken use
 * different stores and silently dropped the token — see git history). getAccessToken() is what the
 * apps' API clients read to authorize their own calls.
 */

const TOKEN_KEY = 'dsp.accessToken';
const REFRESH_KEY = 'dsp.refreshToken';

function storeTokens(accessToken: string, refreshToken?: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  } catch {
    // storage unavailable — token lives for this page session only
  }
}

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function clearTokens(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    // ignore
  }
}

/** The platform's user shape on login + userInfo. */
interface PlatformUser {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId: string;
  userRole?: string;
}

function toAuthUser(u: PlatformUser): AuthUser {
  return {
    id: u.userId,
    email: u.email,
    firstName: u.firstName ?? null,
    lastName: u.lastName ?? null,
    organizationId: u.organizationId,
    roles: u.userRole ? [u.userRole] : [],
    permissions: [],
  };
}

interface AuthPayload {
  user?: PlatformUser & { mfaEnabled?: boolean };
  accessToken?: string;
  refreshToken?: string;
  requiresMFA?: boolean;
}

interface AuthResponse extends AuthPayload {
  data?: AuthPayload;
}

class PlatformAuthError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'PlatformAuthError';
  }
}

async function platformFetch<T>(
  path: string,
  opts: { method: string; body?: unknown; authed?: boolean }
): Promise<T> {
  const { apiBaseUrl } = getAuthConfig();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.authed) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const init: RequestInit = { method: opts.method, headers, credentials: 'include' };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

  const res = await fetch(`${apiBaseUrl}${path}`, init);
  const body = (await res.json().catch(() => ({}))) as T & {
    success?: boolean;
    error?: unknown;
    message?: string;
  };
  if (!res.ok) {
    const nestedError =
      typeof body.error === 'object' && body.error !== null && 'message' in body.error
        ? String(body.error.message)
        : null;
    throw new PlatformAuthError(
      (typeof body.error === 'string' ? body.error : nestedError) ??
        body.message ??
        `Auth request ${path} failed: ${res.status}`,
      res.status
    );
  }
  return body;
}

function finalize(body: AuthResponse): AuthUser {
  const d = body.data ?? body;
  if (!d.accessToken || !d.user) throw new Error('LOGIN_FAILED');
  storeTokens(d.accessToken, d.refreshToken);
  return toAuthUser(d.user);
}

export type PasswordLoginResult =
  { status: 'authenticated'; user: AuthUser } | { status: 'mfa_required' };

/** Email/password login. The platform explicitly reports whether a second factor is required. */
export async function loginWithPassword(
  email: string,
  password: string
): Promise<PasswordLoginResult> {
  const body = await platformFetch<AuthResponse>('/api/v1/auth/login', {
    method: 'POST',
    body: { email, password },
  });
  const payload = body.data ?? body;
  if (payload.requiresMFA === true || payload.user?.mfaEnabled === true) {
    return { status: 'mfa_required' };
  }
  return { status: 'authenticated', user: finalize(body) };
}

/** Complete the platform's cookie-backed MFA challenge. */
export async function verifyMfa(code: string): Promise<AuthUser> {
  const body = await platformFetch<AuthResponse>('/api/v1/auth/mfa/verify', {
    method: 'POST',
    body: { code, type: 'totp' },
  });
  const payload = body.data ?? body;
  if (payload.accessToken && payload.user) return finalize(body);
  if (payload.accessToken) storeTokens(payload.accessToken, payload.refreshToken);
  const user = await fetchCurrentUser();
  if (!user) throw new Error('MFA verification succeeded but no user session was returned.');
  return user;
}

/** Exchange a same-tab Google authorization code through the platform Worker. */
export async function loginWithGoogleRedirect(
  code: string,
  codeVerifier: string,
  nonce: string
): Promise<AuthUser> {
  const body = await platformFetch<AuthResponse>('/api/v1/auth/google/redirect-exchange', {
    method: 'POST',
    body: { code, codeVerifier, nonce },
  });
  if ((body.data ?? body).requiresMFA) {
    throw new Error('Use password sign-in to complete two-step verification.');
  }
  return finalize(body);
}

interface GoogleConfigResponse {
  data?: GoogleRedirectConfig;
}

/** Discovery stays unavailable until both the ID and secret are configured. */
export async function fetchGoogleRedirectConfig(): Promise<GoogleRedirectConfig | null> {
  try {
    const body = await platformFetch<GoogleConfigResponse>('/api/v1/auth/google/redirect-config', {
      method: 'GET',
    });
    const config = body.data;
    return config?.clientId && config.redirectUri ? config : null;
  } catch {
    return null;
  }
}

interface UserInfoResponse {
  data?: { user?: PlatformUser };
}

async function fetchCurrentUser(): Promise<AuthUser | null> {
  const body = await platformFetch<UserInfoResponse>('/api/v1/userInfo', {
    method: 'GET',
    authed: true,
  });
  const user = body.data?.user;
  return user ? toAuthUser(user) : null;
}

/** Resolve the current user from a stored token, or null if not signed in / token invalid. */
export async function getCurrentUser(): Promise<AuthUser | null> {
  if (!getAccessToken()) return null;
  try {
    return await fetchCurrentUser();
  } catch (error) {
    if (error instanceof PlatformAuthError && (error.status === 401 || error.status === 403)) {
      clearTokens();
      return null;
    }
    throw error;
  }
}

export async function logout(): Promise<void> {
  clearTokens();
  return Promise.resolve();
}

/** Request a password-reset email (platform never reveals whether the email exists). */
export async function forgotPassword(email: string): Promise<string> {
  const body = await platformFetch<{
    success?: boolean;
    message?: string;
    data?: { message?: string };
  }>('/api/v1/auth/forgot-password', { method: 'POST', body: { email } });
  return (
    body.message ??
    body.data?.message ??
    'If an account with that email exists, a password reset link has been sent.'
  );
}

/** Check whether a reset token is still valid before showing the new-password form. */
export async function validateResetToken(token: string): Promise<boolean> {
  try {
    const body = await platformFetch<{
      success?: boolean;
      valid?: boolean;
      data?: { valid?: boolean };
    }>('/api/v1/auth/validate-reset-token', { method: 'POST', body: { token } });
    return body.valid === true || body.data?.valid === true || body.success === true;
  } catch {
    return false;
  }
}

/** Complete a forgot-password or invite set-password flow with the emailed token. */
export async function resetPassword(token: string, newPassword: string): Promise<string> {
  const body = await platformFetch<{
    success?: boolean;
    message?: string;
    data?: { message?: string };
  }>('/api/v1/auth/reset-password', { method: 'POST', body: { token, newPassword } });
  return body.message ?? body.data?.message ?? 'Password updated. You can sign in now.';
}
